// Pure rules for the family screens: routing settings, the Unidentified tab,
// Send to company and the admin-panel family switch. The server decides — every
// route here answers 404 unless the caller's family runs the multi-company code
// — and these only decide whether a screen is worth asking about, and read what
// the server said.

import { currentCompany } from './companies';
import { isValidCompanyId } from './companySession';

const OWNER = 'Owner';
const SUPPORT = 'Support';
const ACCOUNTANT = 'Accountant';

/** Who edits routing (spec §6). */
export const ROUTING_EDITOR_ROLES = [OWNER, SUPPORT];
/** Who works the Unidentified tab and Send to company (spec §6, §8.6). */
export const FAMILY_REVIEWER_ROLES = [OWNER, SUPPORT, ACCOUNTANT];

const payloadOf = (response) => response?.data?.payload?.data ?? response?.data?.payload;

/** The API's code for a refusal, at `payload.data.code`. */
export function apiErrorCode(result) {
  const body = result?.response?.data ?? result?.data;
  return body?.payload?.data?.code ?? body?.payload?.code ?? null;
}

/** The API's own message for a refusal, else the fallback. */
export function apiErrorMessage(result, fallback) {
  return (
    result?.response?.data?.metadata?.message || result?.data?.metadata?.message || fallback
  );
}

const roleIn = (myCompanies, companyId, roles) =>
  roles.includes(currentCompany(myCompanies, companyId)?.role);

export const isPrimaryCompany = (myCompanies, companyId) =>
  currentCompany(myCompanies, companyId)?.isPrimary === true;

/** One upper-cased, trimmed prefix — how the server stores them (spec §8.3). */
export const normalizePrefix = (value) => String(value ?? '').trim().toUpperCase();

/** "26, 27 ,26" → ['26', '27']: the comma-separated text field as a list. */
export function parsePrefixes(text) {
  const seen = new Set();
  return String(text ?? '')
    .split(',')
    .map(normalizePrefix)
    .filter((p) => p && !seen.has(p) && seen.add(p));
}

const emptyAddress = (a) => !String(a?.line1 ?? '').trim() && !String(a?.zip ?? '').trim();

const cleanAddress = (a) => ({
  line1: String(a?.line1 ?? '').trim(),
  city: String(a?.city ?? '').trim(),
  zip: String(a?.zip ?? '').trim()
});

/**
 * The routing settings out of `GET /api/v3/company/routing`, or null when the
 * route is not there for this caller (classic family, or not allowed).
 * `familyCompanies` / `hasAttachedCompanies` are read when the server sends them.
 */
export function readRouting(response) {
  if (response?.status !== 200) return null;
  const data = payloadOf(response);
  if (!data || typeof data !== 'object') return null;
  return {
    enabled: data.enabled === true,
    useShipTo: data.useShipTo === true,
    poPrefixes: Array.isArray(data.poPrefixes) ? data.poPrefixes.map(normalizePrefix) : [],
    shipToAddresses: Array.isArray(data.shipToAddresses)
      ? data.shipToAddresses.map(cleanAddress)
      : [],
    familyCompanies: Array.isArray(data.familyCompanies) ? data.familyCompanies : null,
    hasAttachedCompanies:
      typeof data.hasAttachedCompanies === 'boolean' ? data.hasAttachedCompanies : null
  };
}

/**
 * Whether the family has a company attached to its primary. The routing answer
 * says so when it carries the family; otherwise my-companies can show it — the
 * company in hand is an attached one, or the person also belongs to one.
 */
export function familyHasAttachedCompanies(routing, myCompanies, companyId) {
  if (routing?.hasAttachedCompanies !== null && routing?.hasAttachedCompanies !== undefined) {
    return routing.hasAttachedCompanies;
  }
  if (Array.isArray(routing?.familyCompanies)) return routing.familyCompanies.length > 1;
  const companies = myCompanies?.companies ?? [];
  if (currentCompany(myCompanies, companyId)?.isPrimary === false) return true;
  return companies.some((c) => c?.isPrimary === false);
}

/** Worth asking the routing route at all: a switched-on family, as Owner or Support. */
export const mayEditRouting = (myCompanies, companyId) =>
  myCompanies?.multiCompany === true && roleIn(myCompanies, companyId, ROUTING_EDITOR_ROLES);

/** Routing settings: the route answered, and the family has something to route to. */
export const showRoutingSettings = (myCompanies, companyId, routing) =>
  mayEditRouting(myCompanies, companyId) &&
  Boolean(routing) &&
  familyHasAttachedCompanies(routing, myCompanies, companyId);

/** The PUT body: prefixes always; the two switches only on the primary. */
export function routingPayload(form, { primary }) {
  return {
    poPrefixes: parsePrefixes(form.prefixText),
    shipToAddresses: (form.shipToAddresses || []).filter((a) => !emptyAddress(a)).map(cleanAddress),
    ...(primary ? { enabled: form.enabled === true, useShipTo: form.useShipTo === true } : {})
  };
}

/** Worth asking for the Unidentified tab: the primary, as Owner, Support or an Accountant. */
export const mayHaveUnidentifiedTab = (myCompanies, companyId) =>
  myCompanies?.multiCompany === true &&
  isPrimaryCompany(myCompanies, companyId) &&
  roleIn(myCompanies, companyId, FAMILY_REVIEWER_ROLES);

/** `{rows, total}` out of the unidentified list, or null when the route is not there. */
export function readUnidentified(response) {
  if (response?.status !== 200) return null;
  const data = payloadOf(response);
  if (!data || typeof data !== 'object') return null;
  const rows = Array.isArray(data)
    ? data
    : data.documents ?? data.rows ?? data.data ?? [];
  const total =
    data.pagination?.total ?? data.total ?? (Array.isArray(rows) ? rows.length : 0);
  return {
    rows: Array.isArray(rows) ? rows : [],
    total: Number(total) || 0,
    hasAttachedCompanies:
      typeof data.hasAttachedCompanies === 'boolean' ? data.hasAttachedCompanies : null
  };
}

/** The status every row of the Unidentified tab shows. */
export const UNIDENTIFIED_STATUS = 'Unidentified';

/**
 * One Unidentified-tab row in the shape the shared documents table reads (a
 * documents-list row). Tier B now sends that shape; an older Tier B sent only
 * id / originalFileName / documentType / createdAt / routing, so the content the
 * table shows falls back to what routing kept (`routing.summary`). Either way the
 * status is Unidentified: the document is held off every status tab.
 */
export function toUnidentifiedListRow(row) {
  const summary = row?.routing?.summary || {};
  const content = row?.standardizeContent || {
    vendor: summary.vendor ?? '',
    document_number: summary.documentNumber ?? '',
    purchase_order: summary.purchaseOrder ?? '',
    total: summary.total ?? '',
    date: summary.date ?? ''
  };
  return {
    ...row,
    _id: row?._id ?? row?.id,
    standardizeContent: content,
    documentStatus: UNIDENTIFIED_STATUS
  };
}

/** The tab shows when the route answered and the family has an attached company. */
export function showUnidentifiedTab(myCompanies, companyId, unidentified) {
  if (!mayHaveUnidentifiedTab(myCompanies, companyId) || !unidentified) return false;
  if (unidentified.hasAttachedCompanies !== null) return unidentified.hasAttachedCompanies;
  return unidentified.total > 0 || familyHasAttachedCompanies(null, myCompanies, companyId);
}

/** The companies this person can Send to: others where they are also Owner/Support/Accountant. */
export function sendToCompanyTargets(myCompanies, companyId) {
  if (myCompanies?.multiCompany !== true) return [];
  const current = currentCompany(myCompanies, companyId);
  if (!current || !FAMILY_REVIEWER_ROLES.includes(current.role)) return [];
  return (myCompanies.companies ?? []).filter(
    (c) =>
      String(c.companyId) !== String(current.companyId) &&
      FAMILY_REVIEWER_ROLES.includes(c.role) &&
      isValidCompanyId(String(c.companyId))
  );
}

/** `{moved, copied, refused}` out of send-to-company, each an array. */
export function readSendResult(response) {
  const data = payloadOf(response) || {};
  const list = (value) => (Array.isArray(value) ? value : []);
  return { moved: list(data.moved), copied: list(data.copied), refused: list(data.refused) };
}

/** The admin family switch state, or null. */
export function readFamilySwitch(response) {
  if (response?.status !== 200) return null;
  const data = payloadOf(response);
  if (!data || typeof data !== 'object') return null;
  return {
    companyId: data.companyId ?? null,
    enabled: data.enabled === true,
    active: data.active === true,
    frozen: data.frozen === true,
    enabledBy: data.enabledBy ?? null,
    enabledAt: data.enabledAt ?? null,
    membersInOtherCompanies: Number(data.membersInOtherCompanies) || 0
  };
}
